import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";

const TermsOfService = () => {
  const navigate = useNavigate();

  return (
  <div className="min-h-screen bg-background">
    <Header />
    <main className="container mx-auto px-4 py-24 max-w-3xl">
      <Button variant="ghost" size="sm" className="mb-6 -ml-2" onClick={() => navigate(-1)}>
        <ArrowLeft className="w-4 h-4 mr-1" />
        Back
      </Button>
      <h1 className="text-3xl font-display font-bold text-foreground mb-6">Terms of Service</h1>
      <div className="prose text-muted-foreground space-y-4 text-sm">
        <p>Last updated: October 2026</p>

        <p>
          Welcome to Jynio. These Terms of Service ("Terms") govern your access to and use of the
          jynio.co website and platform (the "Service") operated by Jynio ("we", "us", or "our").
          Please read these Terms carefully before using the Service. By creating an account or using
          the Service, you agree to be bound by these Terms, our{" "}
          <a href="/privacy" className="underline">Privacy Policy</a>, and our{" "}
          <a href="/cookies" className="underline">Cookie Policy</a>.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">1. Eligibility</h2>
        <p>
          You must be at least 18 years old to create an account, as a student or as a tutor. By
          creating an account, you confirm that you are at least 18 and that all information you
          provide is accurate and complete.
        </p>
        <p>
          A parent or legal guardian may let their child take part in a lesson that they booked from
          their own account. The parent or guardian stays responsible for the child during the
          lesson, and the child never gets an account of their own.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">2. Description of Service</h2>
        <p>
          Jynio is a two-sided online marketplace that connects students with tutors for live language
          lessons, structured courses, and practice tools such as flashcards. The Service includes:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Browsing and booking live one-on-one or recurring lessons with tutors.</li>
          <li>Purchasing and accessing pre-recorded courses created by tutors.</li>
          <li>Communicating with tutors via in-platform messaging.</li>
          <li>Using flashcards and other learning tools.</li>
          <li>Participating in the Jynio community features.</li>
        </ul>
        <p>
          Jynio acts as a platform facilitating the connection between students and tutors. We are not
          a party to the teaching relationship and do not guarantee the quality, accuracy, or outcomes
          of any lesson or course.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">3. User Accounts</h2>
        <p>
          You must create an account to use most features of the Service. You are responsible for
          maintaining the confidentiality of your login credentials and for all activity that occurs
          under your account. You agree to:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Provide accurate and complete information during registration and onboarding.</li>
          <li>Keep your account information up to date.</li>
          <li>Notify us immediately if you suspect unauthorised access to your account.</li>
          <li id="rule-multiple-accounts" className="scroll-mt-24">Not create multiple accounts or share your account with others. Letting your child
            take part in a lesson you booked, as described in Section 1, is not sharing your account.</li>
        </ul>
        <p>
          We may suspend or close accounts that violate these Terms or our{" "}
          <a href="/community-guidelines" className="underline">Community Guidelines</a>. How we
          decide, what we tell you and how to contest it is in{" "}
          <a href="#how-we-moderate" className="underline">Section 17</a>.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">4. Tutor Accounts</h2>
        <p>
          Tutors must complete an application and onboarding process, which includes providing
          information about their qualifications, teaching experience, country of birth, and a profile
          for public display. By submitting a tutor application, you:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Confirm that you are at least 18 years old.</li>
          <li>Confirm that your qualifications and experience are accurately represented.</li>
          <li>Consent to your profile information (name, biography, teaching experience, education,
            country of origin, languages, and introduction video) being visible to
            students on Jynio, and, if you turn on your public page, to anyone, including
            search engines.</li>
          <li>Understand that your application will be reviewed by our team and may be declined at
            our discretion.</li>
        </ul>
        <p id="how-tutors-are-ordered" className="scroll-mt-24">
          <strong>How tutors are listed and ordered.</strong> Tutors we have approved, whose account
          is active and who have not paused new students, appear in Find a tutor. In every order,
          tutors who can take bookings come first, then the order the student chose (newest, top
          rated, or price), and ties go to the newest tutor. The main factors are therefore whether
          a tutor can take bookings, then the student's choice of order: the date of the application,
          the average teaching score from reviews, or the hourly rate. The page of a language in
          Find a tutor, which visitors without an account can read, shows up to three tutors of that
          language who can take bookings and have turned on their public page, the newest first. No
          tutor pays for a better place, and Jynio's commission has no effect on the order. A tutor leaves the list when they
          pause new students, when they delete their account, after three years without activity, or
          after a check by a person at Jynio for a breach of these Terms. The full and current
          version, including what each badge means, is on{" "}
          <a href="/how-we-order-tutors" className="underline">How we order tutors</a>.
        </p>
        <p id="tutor-community" className="scroll-mt-24">
          <strong>Tutor community.</strong> Jynio runs a community inside the Service where tutors
          talk to each other and to our team. Only tutors whose profile we have approved and not
          switched off can open it, and only while their account is active: access stops while a
          profile is switched off, and when an account is suspended, banned or deleted. Posts and
          replies are seen only by its members and by Jynio staff, and section 8 of our{" "}
          <a href="/community-guidelines#tutor-community" className="underline">Community Guidelines</a>{" "}
          applies to them. Posts are not checked before they appear, except that a post that names a
          student's diagnosis or learning difficulty is held until a person at Jynio has looked at it.
          Our team reads what is posted, a person at Jynio reviews every report and decides every
          removal, and nothing automatic removes a post. The team can pin a post, close a finished
          thread and mark where a problem or an idea stands. If a post or reply breaks section 8 of
          the Community Guidelines or these Terms, we can remove it, or remove its author from the
          community for a set time or with no end date, separately from their account, lessons and
          payouts. Each time, we tell the author what we did, which rule applies, the facts and how to
          contest it, we tell whoever reported it what we decided, and if we got it wrong we put it
          back and tell the author. How we keep and use community data is in our{" "}
          <a href="/privacy#tutor-community" className="underline">Privacy Policy</a>.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">5. Plans, Credits & Payments</h2>
        <p>
          Lessons on Jynio are booked with credits. Credits come with a plan, and a plan is a
          subscription to one tutor. Credits are not transferable between accounts.
        </p>

        <h3 id="plans" className="scroll-mt-24 text-base font-semibold text-foreground mt-4">How a plan works</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>You choose how many lessons a week you want, from 1 to 7. Each lesson a week gives
            4 credits per payment, so 2 lessons a week give 8 credits.</li>
          <li>One credit is one 50-minute lesson, or two 25-minute lessons. The price of a credit is
            the tutor's rate for a 50-minute lesson on the day you subscribe.</li>
          <li>Prices are shown and charged in US dollars. Any tax that applies where you live is added
            on the payment page, which shows the total before you pay. For a one-off payment (extra
            credits, a trial or transfer difference, a course), the payment page may offer your local
            currency at Stripe's exchange rate, which includes a conversion fee; you can pay in
            dollars instead.</li>
          <li>A plan renews by itself every 4 weeks (13 payments a year), at the same price, until
            you cancel it. A plan started as a monthly plan renews every month. We email you a
            receipt for each payment. The price per payment and the matching monthly figure are
            shown before you subscribe.</li>
          <li>A trial lesson is paid at the tutor's price, from your wallet first and then on the
            payment page. Your first 2 trials, each with a different tutor you have no plan with,
            are refundable: if you are not satisfied, ask within 48 hours after the lesson and up
            to $50 for a 50-minute lesson goes back to your wallet; the rest of the price stays
            paid. Subscribing to the trial's tutor ends that refund. Any later trial is paid and
            is not refunded.</li>
          <li>Tutors set their own hourly rates. Jynio charges a platform fee, which is deducted
            before tutor payouts.</li>
        </ul>
        <p>
          All payments are processed through our third-party payment providers. We do not store your
          full payment card details on our servers. By making a purchase, you agree to the payment
          provider's terms of service.
        </p>

        <h3 id="price-changes" className="scroll-mt-24 text-base font-semibold text-foreground mt-4">When a tutor changes the price</h3>
        <p>
          A tutor can propose a new price for your plan. You have 3 days to answer. If you accept,
          the new price applies from the next renewal, and the payment you already made is not
          charged again. If you refuse, or don't answer, nothing changes. A tutor cannot propose a
          higher price within 3 days of your next renewal, and a change to a tutor's public rate
          never changes a plan that already exists.
        </p>

        <h3 id="credits" className="scroll-mt-24 text-base font-semibold text-foreground mt-4">Credits</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Credits you have not used by the end of the billing period they were paid for carry
            over to the next billing period, and then expire.</li>
          <li>Credits belong to the plan they came with, and so to one tutor. When a plan ends,
            credits you have not used can no longer be used.</li>
          <li>You can add extra credits to an active plan: 1 to 12 credits at a time, up to 2
            purchases per billing period. They expire at the same time as that period's credits.</li>
          <li>If a lesson is refunded after its credits have expired, the credit comes back for up
            to 14 days.</li>
          <li>You can move the credits you have left to a tutor you have already had a trial with.
            If the new tutor's rate is lower, the difference goes to your wallet. If it is higher,
            you pay the difference, with your wallet used first. Wallet credit cannot be withdrawn
            as cash and is used first at your next renewal.</li>
        </ul>

        <h3 id="pause" className="scroll-mt-24 text-base font-semibold text-foreground mt-4">Pausing a plan</h3>
        <p>
          You can pause a plan for 2 weeks to 3 months. Nothing is charged while it is paused, the
          next payment moves later by the length of the pause, and the plan starts again by itself at
          the end of it. You can resume earlier. You can pause your plan with the same tutor once
          every 12 months. While a plan is paused you cannot book with it, and the time your credits
          stay valid is extended by the length of the pause. A pause does not cancel lessons you have
          already booked.
        </p>

        <h3 id="cancel" className="scroll-mt-24 text-base font-semibold text-foreground mt-4">Cancelling a plan</h3>
        <p>
          You can cancel a plan at any time, for free, from the plan's card under Credits &
          Subscriptions in your account menu, including while it is paused. The plan ends at the end
          of the period you have paid for. Until then you can keep booking with it, and lessons you
          have already booked stay booked. After the plan has ended, credits you have not used can
          no longer be used. We confirm the cancellation by email, and until the end date you can
          undo it from the plan's card or from that email.
        </p>

        <h3 id="payment-problems" className="scroll-mt-24 text-base font-semibold text-foreground mt-4">When a payment fails</h3>
        <p>
          If a renewal payment fails, the plan is marked past due and we email you. You cannot book
          with it until you update your card. Our payment provider tries the card again several
          times over about two weeks; if the payment still fails, the plan ends.
        </p>

        <h3 id="withdrawal" className="scroll-mt-24 text-base font-semibold text-foreground mt-4">Your 14-day right to withdraw</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>You can withdraw from a plan, from extra credits or from a paid trial within 14 days of
            buying it, without giving a reason. This is the same for every student, wherever you
            live. The 14 days start on the day you buy.</li>
          <li>To withdraw, open Credits &amp; Subscriptions in your account and use the link
            "Withdraw from contract here" next to the purchase, then press "Confirm withdrawal". It
            is there for as long as the 14 days run. We email you an acknowledgment with the date and
            time of your statement, and refund you, to the card you paid with, within 14 days of
            getting it. You can still write to <a href="mailto:hello@jynio.co" className="underline">hello@jynio.co</a>{" "}
            and say which purchase you withdraw from, or send us the model withdrawal form below.</li>
          <li>When you buy, you can ask for your lessons to start straight away, before the 14 days
            are over. If you asked and then withdraw, we keep the price of the lessons you have
            already taken and refund the rest. If you did not ask, you get a full refund, even if
            you have taken lessons.</li>
        </ul>

        <h3 id="withdrawal-form" className="scroll-mt-24 text-base font-semibold text-foreground mt-4">Model withdrawal form</h3>
        <p>
          Model withdrawal form (fill in and send it only if you want to withdraw):
        </p>
        <ul className="list-none pl-6 space-y-1">
          <li>To Jynio, <a href="mailto:hello@jynio.co" className="underline">hello@jynio.co</a>:</li>
          <li>I hereby give notice that I withdraw from my contract for the following service: [plan,
            extra credits or paid trial, and the tutor].</li>
          <li>Ordered on: [date].</li>
          <li>Name of the student: [name].</li>
          <li>Address of the student (only if this form is sent on paper): [address].</li>
          <li>Signature of the student (only if this form is sent on paper): [signature].</li>
          <li>Date: [date].</li>
        </ul>

        <h3 id="refunds" className="scroll-mt-24 text-base font-semibold text-foreground mt-4">Refunds</h3>
        <ul className="list-disc pl-6 space-y-1">
          <li>Lesson credits come back as described in Section 6 (cancelling a lesson more than 24
            hours ahead, or a lesson cancelled by the tutor).</li>
          <li>Your right to withdraw is described above.</li>
          <li>Beyond that right and what the law requires, we do not refund plan payments or extra
            credits. Paid trials and courses keep the refund rules shown when you buy them.</li>
        </ul>

        <h2 className="text-lg font-semibold text-foreground mt-6">6. Booking, Cancellation & Refunds</h2>
        <p>
          When you book a lesson, you agree to the following:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Cancellation by student:</strong> lessons cancelled more than 24 hours before
            the scheduled start time will receive a full credit refund. Cancellations within 24 hours
            may forfeit the credits used.</li>
          <li><strong>Cancellation by tutor:</strong> if a tutor cancels a lesson, your credits will
            be fully refunded regardless of timing.</li>
          <li><strong>No-shows:</strong> if a student or tutor fails to attend a scheduled lesson
            without prior cancellation, the platform's standard cancellation policy applies.</li>
          <li><strong>Recurring bookings:</strong> recurring lessons can be modified or cancelled at
            any time. Changes take effect from the next scheduled lesson.</li>
        </ul>
        <p>
          For disputes regarding lesson quality or tutor conduct, please contact us at{" "}
          <a href="mailto:hello@jynio.co" className="underline">hello@jynio.co</a>. We will review
          each case individually and may issue credit refunds at our discretion.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">7. Course Content & Intellectual Property</h2>
        <p>
          The Service contains content owned by Jynio, tutors, and third parties. The following
          intellectual property terms apply:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li><strong>Jynio platform:</strong> the Service, its design, branding, and software are
            owned by Jynio and protected by intellectual property laws. You may not copy, modify, or
            distribute any part of the platform without our written consent.</li>
          <li><strong>Tutor-created content:</strong> tutors retain ownership of the courses, lesson
            materials, and other content they create. By publishing content on Jynio, tutors grant us
            a non-exclusive, worldwide licence to host, display, and distribute that content through
            the platform for the purpose of delivering the Service.</li>
          <li><strong>Tutor community posts:</strong> you keep ownership of what you post in the tutor
            community. Your posts and replies are shown only to its members and to Jynio staff, never
            publicly. You give Jynio a non-exclusive, worldwide, royalty-free licence to host, show
            and translate them (including automatically) inside the community for as long as they stay
            there, including after your account is deleted, when they show "Deleted User". You also
            allow Jynio to keep a copy for the periods in our Privacy Policy, including after you
            delete a post, to handle reports and enforce these rules. You can delete your own posts
            and replies at any time.</li>
          <li><strong>Student content:</strong> any reviews or other content you submit may be
            displayed publicly on the platform. You grant Jynio a non-exclusive licence to use this
            content in connection with the Service.</li>
          <li><strong>Account deletion and courses:</strong> if a tutor deletes their account, they
            may choose to remove their courses from sale or allow them to remain available on the
            platform. If courses remain live after account deletion, revenue terms will be as agreed
            at the time of deletion.</li>
        </ul>

        <h2 className="text-lg font-semibold text-foreground mt-6">8. Prohibited Conduct</h2>
        <p>
          You agree not to use the Service to:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li id="rule-harassment" className="scroll-mt-24">Harass, abuse, threaten, or discriminate against any user.</li>
          <li id="rule-false-information" className="scroll-mt-24">Post false, misleading, or fraudulent information, including fake reviews or credentials.</li>
          <li id="rule-off-platform" className="scroll-mt-24">Arrange lessons, payments, or transactions outside the platform to circumvent our fees.</li>
          <li id="rule-recording" className="scroll-mt-24">Record live sessions without the explicit consent of all participants.</li>
          <li id="rule-contact-details" className="scroll-mt-24">Share personal contact information or payment details outside the platform's messaging system.</li>
          <li id="rule-intellectual-property" className="scroll-mt-24">Upload or distribute content that infringes third-party intellectual property rights.</li>
          <li id="rule-unauthorized-access" className="scroll-mt-24">Attempt to gain unauthorised access to other users' accounts or the platform's systems.</li>
          <li id="rule-spam" className="scroll-mt-24">Send spam or unwanted promotions, or run accounts that send messages automatically.</li>
          <li id="rule-unlawful" className="scroll-mt-24">Use the Service for any unlawful purpose.</li>
        </ul>
        <p>
          What we do about a violation, what we tell you and how you can contest it is in{" "}
          <a href="#how-we-moderate" className="underline">Section 17</a>.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">9. Live Sessions & Communications</h2>
        <p>
          Live lessons are conducted through the platform's session interface, which may include video,
          chat, whiteboard, and note-taking features. By participating in a live session, you
          acknowledge that:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Session content (chat messages, shared notes, whiteboard data) may be retained to
            support the learning experience.</li>
          <li>You must not record any session without the explicit consent of all participants.</li>
          <li>Jynio does not monitor live sessions in real time but reserves the right to investigate
            reported misconduct.</li>
        </ul>

        <h2 className="text-lg font-semibold text-foreground mt-6">10. Account Suspension & Termination</h2>
        <p>
          We may suspend or close your account if you violate these Terms or our Community
          Guidelines, or if required by law. How we decide, what we tell you and how to contest it is
          in <a href="#how-we-moderate" className="underline">Section 17</a>. You may also delete
          your account at any time through your account settings. Upon account deletion:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Your profile will be hidden from other users immediately.</li>
          <li>You will have a 30-day grace period during which you can reactivate your account.</li>
          <li>After 30 days, your account and associated data will be permanently deleted in
            accordance with our <a href="/privacy" className="underline">Privacy Policy</a>.</li>
          <li>Any remaining credit balance will be forfeited after the 30-day grace period.</li>
          <li>When you ask to delete your account, each plan you pay for is set to end when its
            current paid period runs out, so nothing renews during the grace period. Credits you
            have not used are lost when the deletion is completed. If you reactivate your account
            during the grace period, your plans carry on as before.</li>
        </ul>

        <h2 className="text-lg font-semibold text-foreground mt-6">11. Referral Programme</h2>
        <p>
          Jynio may offer a referral programme allowing you to invite others to the platform. Referral
          credits are subject to the following conditions:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>Referral rewards are credited only after the referred user creates an account and
            their subscription renews for the first time.</li>
          <li>Referral credits cannot be exchanged for cash.</li>
          <li>We reserve the right to modify or discontinue the referral programme at any time.</li>
          <li id="rule-referral-abuse" className="scroll-mt-24">Abuse of the referral system (e.g. creating fake accounts) will result in forfeiture
            of credits and potential account suspension.</li>
        </ul>

        <h2 className="text-lg font-semibold text-foreground mt-6">12. Disclaimers</h2>
        <p>
          The Service is provided "as is" and "as available" without warranties of any kind, whether
          express or implied. We do not warrant that:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>The Service will be uninterrupted, error-free, or secure.</li>
          <li>Any tutor is qualified, certified, or suitable for your learning needs.</li>
          <li>Learning outcomes or language proficiency improvements will be achieved.</li>
          <li>Course content is accurate, complete, or up to date.</li>
        </ul>
        <p>
          Jynio is a marketplace platform. We do not employ tutors and are not responsible for the
          content, quality, or conduct of any tutor or student on the platform.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">13. Limitation of Liability</h2>
        <p>
          To the maximum extent permitted by applicable law, Jynio and its officers, directors,
          employees, and agents shall not be liable for any indirect, incidental, special,
          consequential, or punitive damages arising out of or related to your use of the Service.
          Our total liability for any claim arising from these Terms or the Service shall not exceed
          the amount you have paid to Jynio in the 12 months preceding the claim.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">14. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless Jynio and its officers, directors, employees, and
          agents from any claims, damages, losses, or expenses (including reasonable legal fees)
          arising from your use of the Service, your violation of these Terms, or your infringement
          of any third-party rights.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">15. Changes to These Terms</h2>
        <p>
          We may update these Terms from time to time. We will notify you of material changes by
          posting the updated Terms on this page with a revised "Last updated" date, and by telling
          you by email or in the app. Tutors are told 15 days or more before a change that
          affects them applies, unless the law requires a change sooner. Your continued
          use of the Service after changes take effect constitutes acceptance of the revised Terms.
          If you do not agree with the changes, you should stop using the Service and delete your
          account.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">16. Governing Law</h2>
        <p>
          These Terms are governed by and construed in accordance with the laws of the jurisdiction
          in which Jynio is established, without regard to conflict of law principles. Any disputes
          arising from these Terms or the Service shall be resolved in the competent courts of that
          jurisdiction, subject to any mandatory consumer protection laws that may apply in your
          country of residence.
        </p>

        <h2 id="how-we-moderate" className="scroll-mt-24 text-lg font-semibold text-foreground mt-6">17. How We Moderate</h2>
        <p>
          This section says how Jynio applies these Terms and our{" "}
          <a href="/community-guidelines" className="underline">Community Guidelines</a>: what we
          can do, who decides, what you are told and how to ask us to look again.
        </p>

        <h3 className="text-base font-semibold text-foreground mt-4">The rules</h3>
        <p>
          The rules are in Section 8 (Prohibited Conduct), the rule against several accounts in
          Section 3, the referral rules in Section 11, and the Community Guidelines, including their
          Section 8 for the tutor community. Each time we restrict something, we name the rule and
          link to it.
        </p>

        <h3 className="text-base font-semibold text-foreground mt-4">What we can do</h3>
        <p>From the lightest step to the heaviest, we can:</p>
        <ul className="list-disc pl-6 space-y-1">
          <li>remove a message, the text of a review, a photo, or a post or reply in the tutor
            community;</li>
          <li>hide a tutor from search results while they put something right;</li>
          <li>switch off a tutor's profile, so students can no longer find or book them;</li>
          <li>suspend an account, so its owner can no longer sign in;</li>
          <li>close an account.</li>
        </ul>
        <p>
          We use the lightest step that deals with the problem. For a repeated or serious breach we
          can go straight to a heavier one.
        </p>

        <h3 className="text-base font-semibold text-foreground mt-4">Who decides</h3>
        <p>
          A person at Jynio decides every restriction. Reports from users and our own checks reach a
          person. One automatic check exists: in the tutor community, a post or reply that seems to
          name a student's diagnosis or learning difficulty is held and shown to nobody else until a
          person has looked at it and either approved it or removed it. If we add other automatic
          checks, they will only flag something for a person; they will not restrict anyone by
          themselves.
        </p>

        <h3 className="text-base font-semibold text-foreground mt-4">What you are told, and when</h3>
        <p>
          When we remove your content or restrict your account, we send you a notice by email, in
          the language of your account, at the latest when the restriction starts. It says:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>what we did, and what it changes for you;</li>
          <li>the facts we relied on, and whether a report or our own check led to it. We never say
            who reported it;</li>
          <li>the rule that applies, with a link to it, and, if we removed something because it is
            illegal, the law and why;</li>
          <li>that a person at Jynio decided it, and whether anything automatic was involved;</li>
          <li>how to contest it.</li>
        </ul>
        <p>
          The notice does not repeat what we removed, because it may name another person. Where the
          content was yours in the tutor community, you can read it in the app, where only you can
          see it. Someone who reported something is told what we decided, and how to contest it.
        </p>
        <p>
          We may limit a spam or bot account, which is an account made to send unwanted or automatic
          messages, without sending a notice. We also do not send a notice when the law or an
          authority tells us not to.
        </p>

        <h3 className="text-base font-semibold text-foreground mt-4">If you disagree</h3>
        <p>
          Reply to the notice, or write to <a href="mailto:hello@jynio.co" className="underline">hello@jynio.co</a>.
          A person at Jynio answers within 2 business days. If we got it wrong, we lift the
          restriction and tell you. You can also go to the courts.
        </p>

        <h3 className="text-base font-semibold text-foreground mt-4">For tutors</h3>
        <p>
          A tutor runs a business on Jynio, so we explain more and give more time:
        </p>
        <ul className="list-disc pl-6 space-y-1">
          <li>When we hide your profile from search, switch it off, suspend or close your account,
            the notice gives the reasons, and you can answer them.</li>
          <li>When we close your account because you broke the rules, we suspend it first and close
            it 30 days later. In those 30 days you can explain, and we can call the closing off.</li>
          <li>We can close it at once, with no 30 days, only if you repeat a breach we already told
            you about, if the law requires it, or for an imperative legal reason.</li>
          <li>When your account is closed, your students' plans with you are cancelled, so they stop
            paying for a tutor who can no longer teach them.</li>
        </ul>

        <h3 className="text-base font-semibold text-foreground mt-4">Contact point</h3>
        <p>
          Users, and the authorities of any country where we operate, can reach us about moderation
          and about illegal content at{" "}
          <a href="mailto:hello@jynio.co" className="underline">hello@jynio.co</a>, in English or in
          any language Jynio is offered in. A person reads it.
        </p>

        <h2 className="text-lg font-semibold text-foreground mt-6">18. Contact Us</h2>
        <p>
          If you have any questions about these Terms, please contact us at{" "}
          <a href="mailto:hello@jynio.co" className="underline">hello@jynio.co</a>.
        </p>
      </div>
    </main>
    <Footer />
  </div>
  );
};

export default TermsOfService;
